import * as React from 'react';
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Spinner, Alert, Form } from 'reactstrap';
import { getConsentDataAsync, postApproveConsentsAsync } from '../../ajax/requests';
import { ConsentManagementSettingsData, Consents } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { useConsentSelection } from './useConsentSelection';
import * as S from './ConsentApproval.styles';
import { getConsentLabel as getConsentLabelUtil } from '../../shared/consentUtils';
import TurnstileWidget from '../../components/TurnstileWidget';
import AppButton from '../../components/AppButton';

const ConsentApproval: React.FC = () => {
    const t = useTranslations();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [consentData, setConsentData] = useState<ConsentManagementSettingsData | null>(null);
    const [token, setToken] = useState<string>('');

    const {
        selectedConsents,
        handleConsentToggle,
        handleCheckboxToggle,
        isCheckboxSelected,
        getApprovedCustomCheckboxTexts,
        getConsentLabel,
        validateMandatoryConsents
    } = useConsentSelection();

    const areAllMandatoryConsentsSelected = (): boolean => {
        if (!consentData) return false;
        return consentData.consentsCheckboxes
            .filter(c => c.isMandatory)
            .every(c => isCheckboxSelected(c));
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tokenParam = params.get('token');

        if (!tokenParam) {
            setError(t(TK.consentLinkExpired));
            setLoading(false);
            return;
        }

        setToken(tokenParam);

        getConsentDataAsync(tokenParam)
            .then(data => {
                setConsentData(data);
                setLoading(false);
            })
            .catch(err => {
                setError(err?.response?.data?.Message || t(TK.consentLinkExpired));
                setLoading(false);
            });
    }, [location, t]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!consentData) return;

        const validationError = validateMandatoryConsents(consentData);
        if (validationError) {
            setError(validationError);
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await postApproveConsentsAsync(token, selectedConsents, getApprovedCustomCheckboxTexts());
            setSuccess(true);
        } catch (err: any) {
            setError(err?.response?.data?.Message || t(TK.somethingWentWrong));
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <S.Container>
                <S.LoadingContainer>
                    <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                    <S.LoadingText>{t(TK.loading)}...</S.LoadingText>
                </S.LoadingContainer>
            </S.Container>
        );
    }

    if (success) {
        return (
            <S.Container>
                <S.SuccessContainer>
                    <S.SuccessIcon>✓</S.SuccessIcon>
                    <S.Title>{t(TK.consentApprovalSuccess)}</S.Title>
                    <S.SuccessText>{t(TK.consentApprovalRegistered)}</S.SuccessText>
                    <S.SuccessSubtext>{t(TK.consentApprovalCloseWindow)}</S.SuccessSubtext>
                </S.SuccessContainer>
            </S.Container>
        );
    }

    if (error || !consentData) {
        return (
            <S.Container>
                <Alert color="danger">
                    <h4>{t(TK.error)}</h4>
                    <p>{error || t(TK.consentDataUnavailable)}</p>
                </Alert>
            </S.Container>
        );
    }

    return (
        <S.Container>
            <S.Header>
                {consentData.companyLogo ? (
                    <S.CompanyLogo src={consentData.companyLogo} alt={consentData.companyName || 'Company'} />
                ) : (
                    <S.CompanyName>{consentData.companyName || 'Appointmint'}</S.CompanyName>
                )}
                <S.Title>{t(TK.consentApprovalTitle)}</S.Title>
                <S.Subtitle>{t(TK.consentApprovalSubtitle)}</S.Subtitle>
            </S.Header>

            <S.WarningBox>
                {t(TK.consentApprovalExpires30Min)}
            </S.WarningBox>

            <Form onSubmit={handleSubmit}>
                {consentData.consentsCheckboxes.map((checkbox, checkboxIndex) => {
                    const isSelected = isCheckboxSelected(checkbox);
                    const enabledConsentKeys = (Object.keys(checkbox.consents) as Array<keyof Consents>)
                        .filter(key => checkbox.consents[key]);

                    return (
                        <S.ConsentBox key={checkboxIndex}>
                            {/* Main checkbox */}
                            <S.CheckboxLabel>
                                <S.Checkbox
                                    checked={isSelected}
                                    onChange={(e) => handleCheckboxToggle(checkbox, e.target.checked)}
                                />
                                <S.ConsentText>
                                    {checkbox.text}
                                    {checkbox.isMandatory && <S.MandatoryBadge>({t(TK.mandatory)})</S.MandatoryBadge>}
                                </S.ConsentText>
                            </S.CheckboxLabel>

                            {/* Individual consents within the checkbox */}
                            {enabledConsentKeys.length > 0 && (
                                <S.SubConsentsContainer>
                                    {enabledConsentKeys.map((consentKey) => (
                                        <S.SubConsentItem key={consentKey}>
                                            <S.SubConsentCheckbox
                                                checked={selectedConsents[consentKey]}
                                                onChange={(e) => handleConsentToggle(consentKey, e.target.checked)}
                                            />
                                            <span>{getConsentLabel(consentKey)}</span>
                                        </S.SubConsentItem>
                                    ))}
                                </S.SubConsentsContainer>
                            )}

                            {/* Links */}
                            {checkbox.links && Object.keys(checkbox.links).length > 0 && (
                                <S.LinksContainer>
                                    {Object.entries(checkbox.links).map(([key, url]) => (
                                        <S.LinkItem key={key}>
                                            <S.StyledLink href={url} target="_blank" rel="noopener noreferrer">
                                                {key}
                                            </S.StyledLink>
                                        </S.LinkItem>
                                    ))}
                                </S.LinksContainer>
                            )}
                        </S.ConsentBox>
                    );
                })}

                {/* Global links */}
                {(consentData.termsAndConditionsLink || consentData.privacyPolicyLink) && (
                    <S.LinksContainer>
                        {consentData.termsAndConditionsLink && (
                            <S.StyledLink href={consentData.termsAndConditionsLink} target="_blank" rel="noopener noreferrer">
                                {t(TK.termsAndConditions)}
                            </S.StyledLink>
                        )}
                        {consentData.termsAndConditionsLink && consentData.privacyPolicyLink && ' | '}
                        {consentData.privacyPolicyLink && (
                            <S.StyledLink href={consentData.privacyPolicyLink} target="_blank" rel="noopener noreferrer">
                                {t(TK.privacyPolicy)}
                            </S.StyledLink>
                        )}
                    </S.LinksContainer>
                )}

                {error && (
                    <Alert color="danger" style={{ marginTop: 'var(--spacer-S)' }}>
                        {error}
                    </Alert>
                )}

                <TurnstileWidget />
                
                <S.SubmitButtonContainer>
                    <AppButton
                        type="submit"
                        color="primary"
                        size="lg"
                        disabled={submitting || !areAllMandatoryConsentsSelected()}
                        style={{ minWidth: '200px' }}
                    >
                        {submitting ? <Spinner size="sm" /> : t(TK.approveConsents)}
                    </AppButton>
                </S.SubmitButtonContainer>
            </Form>
        </S.Container>
    );
};

export default ConsentApproval;
